// ============================================
// OMEGA Skills — библиотека встроенных навыков
// Версия: 1.0 Lite (фундамент для OMEGA v5)
// ============================================

// [PLANCONFIG-ADMIN] кэш тарифов для customer_support (5 мин)
let omegaSkillsPlanCache = null

/**
 * Фабрика простых навыков OMEGA.
 * Каждый навык содержит метаданные + execute(params, omega).
 */
export const OMEGA_SKILLS = [
    {
        id: 'price_analysis',
        name: 'Анализ цен',
        description: 'Анализирует цены конкурентов и рекомендует оптимальные тарифы.',
        category: 'finance',
        triggers: ['цена', 'тариф', 'конкурент', 'pricing', 'price'],
        execute: async (params) => {
            const plans = params.plans || []
            const avg = plans.reduce((a, p) => a + (p.price || 0), 0) / (plans.length || 1)
            return {
                recommendations: plans.map(p => ({
                    plan: p.name,
                    current: p.price,
                    suggested: Math.round((p.price || avg) * (p.price < avg ? 1.1 : 0.95)),
                })),
                avgMarketPrice: Math.round(avg),
            }
        },
    },
    {
        id: 'revenue_forecast',
        name: 'Прогноз доходов',
        description: 'Прогнозирует MRR, churn и LTV на основе истории платежей.',
        category: 'finance',
        triggers: ['доход', 'mrr', 'прогноз', 'forecast', 'revenue', 'ltv'],
        execute: async (params) => {
            const payments = params.payments || []
            const income = payments.filter(p => p.type === 'income').reduce((a, b) => a + b.amount, 0)
            const expense = payments.filter(p => p.type === 'expense').reduce((a, b) => a + b.amount, 0)
            const profit = income - expense
            return {
                mrr: Math.round(income / 3),
                profit,
                forecastNextMonth: Math.round(income * 1.05),
                forecastNextQuarter: Math.round(income * 3.2),
                health: profit > 0 ? 'positive' : 'negative',
            }
        },
    },
    {
        id: 'security_monitor',
        name: 'Мониторинг безопасности',
        description: 'Отслеживает подозрительную активность и алерты.',
        category: 'security',
        triggers: ['безопасность', 'алерт', 'взлом', 'security', 'alert', 'login'],
        execute: async (params) => {
            const logs = params.logs || []
            const threats = logs.filter(l => l.severity === 'high' || l.level === 'error')
            return {
                threatLevel: threats.length > 3 ? 'high' : threats.length > 0 ? 'medium' : 'low',
                threatsFound: threats.length,
                recommendations: threats.length > 0
                    ? ['Включить 2FA', 'Проверить активные сессии', 'Обновить API-ключи']
                    : ['Система в безопасности'],
            }
        },
    },
    {
        id: 'content_moderation',
        name: 'Модерация контента',
        description: 'Проверяет контент на нарушения и токсичность.',
        category: 'content',
        triggers: ['контент', 'модерация', 'токсичность', 'moderation', 'content'],
        execute: async (params) => {
            const text = params.text || ''
            const flags = ['spam', 'оскорбление', 'насилие', '18+'].filter(f =>
                text.toLowerCase().includes(f)
            )
            return {
                safe: flags.length === 0,
                flags,
                score: Math.max(0, 100 - flags.length * 25),
            }
        },
    },
    {
        id: 'customer_support',
        name: 'Авто-ответы поддержки',
        description: 'Автоматически отвечает на типовые тикеты.',
        category: 'support',
        triggers: ['поддержка', 'тикет', 'вопрос', 'support', 'help', 'ticket'],
        execute: async (params) => {
            const q = (params.question || '').toLowerCase()
            if (q.includes('цена') || q.includes('тариф')) {
                // [PLANCONFIG-ADMIN] тарифы — живые из PlanConfig (кэш 5 мин), без хардкода цен
                try {
                    const { planConfigApi } = await import('../../services/api.js')
                    if (!omegaSkillsPlanCache || Date.now() - omegaSkillsPlanCache.at > 5 * 60 * 1000) {
                        const res = await planConfigApi.list()
                        omegaSkillsPlanCache = { at: Date.now(), plans: res?.plans || [] }
                    }
                    const lines = omegaSkillsPlanCache.plans.map(p => `${p.plan === 'free' ? 'Free' : p.plan === 'pro' ? 'Pro' : 'Agency'} — ${p.price}₽/мес`)
                    if (lines.length) return { reply: `Наши тарифы: ${lines.join(', ')}. Подробнее — на странице подписки.`, category: 'billing' }
                } catch { /* фолбэк ниже */ }
                return { reply: 'Актуальные тарифы — на странице подписки в личном кабинете.', category: 'billing' }
            }
            if (q.includes('api') || q.includes('ключ')) {
                return { reply: 'API-ключи можно управлять в разделе OMEGA → API Keys.', category: 'technical' }
            }
            return { reply: 'Спасибо за обращение. Оператор ответит вам в ближайшее время.', category: 'general' }
        },
    },
    {
        id: 'campaign_optimizer',
        name: 'Оптимизация рекламных кампаний',
        description: 'Анализирует кампании и рекомендует улучшения.',
        category: 'marketing',
        triggers: ['кампания', 'реклама', 'ctr', 'cpc', 'campaign', 'ads'],
        execute: async (params) => {
            const campaigns = params.campaigns || []
            return campaigns.map(c => ({
                name: c.name,
                status: c.status,
                recommendation: c.ctr < 2 ? 'Улучшить заголовок и CTA'
                    : c.roi < 100 ? 'Пересмотреть аудиторию'
                    : 'Масштабировать',
            }))
        },
    },
    {
        id: 'code_generate',
        name: 'Генерация кода',
        description: 'Генерирует код по запросу (React, Node.js, Python).',
        category: 'dev',
        triggers: ['код', 'сгенерируй', 'react', 'node', 'python', 'code', 'script'],
        execute: async (params) => {
            const lang = params.language || 'javascript'
            return {
                language: lang,
                code: `// Generated ${lang} code for: ${params.prompt || 'task'}\n// TODO: implement`,
                note: 'Это заглушка. Интеграция с AI-провайдером в P12.',
            }
        },
    },
    {
        id: 'report_generate',
        name: 'Генерация отчётов',
        description: 'Создаёт сводные отчёты по метрикам бизнеса.',
        category: 'analytics',
        triggers: ['отчёт', 'report', 'analytics', 'метрики', 'svod'],
        execute: async (params) => {
            const data = params.data || {}
            return {
                title: data.title || 'OMEGA Report',
                generatedAt: new Date().toISOString(),
                sections: ['summary', 'metrics', 'recommendations'],
                summary: data.summary || 'Отчёт сгенерирован на основе доступных данных.',
            }
        },
    },
    {
        id: 'seo_optimize',
        name: 'SEO-оптимизация',
        description: 'Генерирует title, description, теги и хештеги.',
        category: 'content',
        triggers: ['seo', 'теги', 'хештеги', 'описание', 'title', 'hashtags'],
        execute: async (params) => {
            const topic = params.topic || 'AI Viral Studio'
            return {
                title: `${topic}: как получить вирусный рост с AI`,
                description: `Узнайте, как ${topic} помогает создавать вирусный контент и масштабировать бизнес.`,
                tags: ['AI', 'viral', 'marketing', 'content', 'growth'],
                hashtags: ['#AIViralStudio', '#ViralMarketing', '#AIContent', '#GrowthHacking'],
            }
        },
    },
    {
        id: 'notification_design',
        name: 'Дизайн уведомлений',
        description: 'Формулирует уведомления для пользователей.',
        category: 'product',
        triggers: ['уведомление', 'notification', 'message', 'сообщение'],
        execute: async (params) => {
            const event = params.event || 'update'
            return {
                title: event === 'payment' ? 'Платёж прошёл успешно' : 'Новое обновление платформы',
                body: event === 'payment' ? 'Спасибо за оплату. Ваш тариф активен.' : 'Добавлены новые AI-функции. Попробуйте прямо сейчас.',
                channel: params.channel || 'in-app',
            }
        },
    },
]

/**
 * Регистрирует все встроенные навыки в ядре OMEGA.
 */
export function registerDefaultSkills(omegaCore) {
    OMEGA_SKILLS.forEach(skill => omegaCore.registerSkill(skill))
}

export default OMEGA_SKILLS
